import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import {
  Badge,
  Banner,
  Button,
  Drawer,
  Field,
  IdChip,
  QueryState,
  Section,
  Select,
  StatusDot,
  TextArea,
  TextInput,
  Time,
  domainOptions,
} from '@/components/ui';
import {
  RESOLUTION_REASON_LABELS,
  conversationQuery,
  messageQuery,
  resolutionCaseQuery,
  resolutionCasesQuery,
  UNASSIGNED_LIMIT,
  type ResolutionCase,
  type SenderType,
} from './api';
import { MessageArticle } from './MessageArticle';
import { LocationName } from './parts';

/**
 * Incoming mail that could not be placed in a conversation: a compact block at the top of Needs
 * you, rendered only while the queue has items. Each row opens the routing decision in a side panel.
 */
export function UnassignedMail() {
  const orgId = useOrgId();
  const list = useQuery(resolutionCasesQuery(orgId, 'open', undefined, UNASSIGNED_LIMIT));
  const [open, setOpen] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section className="unassigned" aria-labelledby="unassigned-title">
      <h2 id="unassigned-title" className="unassigned-title">
        Mail to place <Badge value={items.length} label="messages to place" attention />
      </h2>
      <ul className="unassigned-rows">
        {items.map((c) => (
          <li key={c.id}>
            <button type="button" className="unassigned-row" onClick={() => setOpen(c.id)}>
              <span className="unassigned-line">
                <span className="unassigned-reason">{RESOLUTION_REASON_LABELS[c.reason_code]}</span>
                <Time value={c.created_at} relative />
              </span>
              <span className="unassigned-sub">
                {c.candidates.length === 0
                  ? 'No matching conversation'
                  : `${c.candidates.length} possible conversation${c.candidates.length === 1 ? '' : 's'}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <Drawer open={open !== null} onClose={() => setOpen(null)} title="Place message">
        {open ? <CaseDetail key={open} caseId={open} /> : null}
      </Drawer>
    </section>
  );
}

function CaseDetail({ caseId }: { caseId: string }) {
  const orgId = useOrgId();
  const q = useQuery(resolutionCaseQuery(orgId, caseId));
  return <QueryState query={q}>{(c) => <CaseWorkspace rc={c} />}</QueryState>;
}

function CaseWorkspace({ rc }: { rc: ResolutionCase }) {
  const orgId = useOrgId();
  const message = useQuery(messageQuery(orgId, rc.message_id));
  return (
    <div className="stack-lg">
      <Section title="Message" headingLevel={3}>
        <QueryState query={message}>{(m) => <MessageArticle message={m} headingLevel={3} />}</QueryState>
      </Section>
      <Section title="Possible conversations" headingLevel={3}>
        {rc.candidates.length === 0 ? (
          <p className="small muted">No matching conversation</p>
        ) : (
          rc.candidates.map((cand) => <CandidateCard key={cand.conversation_id} candidate={cand} />)
        )}
      </Section>
      {rc.state === 'open' ? (
        <ResolveForm rc={rc} />
      ) : (
        <Banner title={rc.state === 'resolved' ? 'Assigned' : 'Ignored'}>{rc.resolution_reason ?? 'No reason recorded'}</Banner>
      )}
    </div>
  );
}

function CandidateCard({ candidate }: { candidate: components['schemas']['ThreadCandidate'] }) {
  const orgId = useOrgId();
  const conv = useQuery(conversationQuery(orgId, candidate.conversation_id));
  return (
    <div className="stack-sm">
      <div className="row-between">
        <Link to="/app/conversations/$conversationId" params={{ conversationId: candidate.conversation_id }} className="strong">
          {conv.data?.title ?? <IdChip id={candidate.conversation_id} label="Conversation" />}
        </Link>
        <span className="row">
          <span className="meta">Matched by {candidate.match_kind.replace(/_/g, ' ')}</span>
          <StatusDot tone={candidate.confidence === 'high' ? 'neutral' : 'faint'}>{candidate.confidence} confidence</StatusDot>
        </span>
      </div>
      <div className="stack-sm small">
        {candidate.evidence.header_facts.length > 0 ? (
          <ul className="unassigned-evidence">
            {candidate.evidence.header_facts.map((f, i) => (
              <li key={i} className="mono xsmall">
                {f}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No header details</p>
        )}
        {candidate.evidence.location_ids?.length ? (
          <p>
            Sites named:{' '}
            {candidate.evidence.location_ids.map((id, i) => (
              <span key={id}>
                {i > 0 ? ', ' : null}
                <LocationName locationId={id} />
              </span>
            ))}
          </p>
        ) : null}
        {candidate.evidence.message_ids.length > 0 ? (
          <p className="muted">Related messages: {candidate.evidence.message_ids.length}</p>
        ) : null}
      </div>
    </div>
  );
}

type Decision = components['schemas']['ThreadResolutionDecision'];

function ResolveForm({ rc }: { rc: ResolutionCase }) {
  const orgId = useOrgId();
  const [decision, setDecision] = useState<Decision | ''>('');
  const [target, setTarget] = useState('');
  const [title, setTitle] = useState('');
  const [senderType, setSenderType] = useState<SenderType | ''>('');
  const [reason, setReason] = useState('');
  const [locDecisions, setLocDecisions] = useState<Record<string, 'associate' | 'reject' | ''>>({});
  const [result, setResult] = useState<components['schemas']['ResolveThreadAssociationResult'] | null>(null);
  const idem = useMemo(() => newIdempotencyKey(), []);
  const locationIds = useMemo(
    () => [...new Set(rc.candidates.flatMap((c) => c.evidence.location_ids ?? []))],
    [rc.candidates],
  );

  const missing =
    !decision ||
    !reason.trim() ||
    (decision === 'assign_existing' && !target) ||
    (decision === 'create_new' && (!title.trim() || !senderType)) ||
    (decision !== 'dismiss' && locationIds.some((id) => !locDecisions[id]));

  const resolve = useCommand({
    run: () =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/thread-resolution-cases/{caseId}/resolve', {
          params: { path: { orgId, caseId: rc.id }, header: { 'If-Match': ifMatch(rc.version), 'Idempotency-Key': idem } },
          body: {
            decision: decision as Decision,
            reason: reason.trim(),
            ...(decision === 'assign_existing' ? { conversation_id: target } : {}),
            ...(decision === 'create_new' ? { new_conversation_title: title.trim(), new_conversation_sender_type: senderType as SenderType } : {}),
            ...(decision !== 'dismiss'
              ? {
                  location_decisions: locationIds.map((id) => ({
                    location_id: id,
                    decision: locDecisions[id] as 'associate' | 'reject',
                    ...(locDecisions[id] === 'associate' ? { relationship: 'discussed' as const } : {}),
                  })),
                }
              : {}),
          },
        }),
      ),
    success: (r) => (r.decision_id ? 'Message assigned' : 'Message ignored'),
    invalidate: [orgKey(orgId, 'thread-resolution-cases'), orgKey(orgId, 'conversations')],
    onSuccess: setResult,
  });

  if (result) {
    return (
      <Banner title="Decision saved">
        {result.conversation_id ? (
          <Link to="/app/conversations/$conversationId" params={{ conversationId: result.conversation_id }}>
            Open conversation
          </Link>
        ) : (
          'Message ignored, kept on record'
        )}
      </Banner>
    );
  }

  return (
    <Section title="Placement" headingLevel={3}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!missing) resolve.mutate(undefined);
        }}
      >
        <fieldset className="stack-sm unassigned-fieldset">
          <legend className="visually-hidden">Placement</legend>
          {(
            [
              ['assign_existing', 'Add to existing conversation'],
              ['create_new', 'Start new conversation'],
              ['dismiss', 'Ignore message (kept on record)'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="checkbox">
              <input type="radio" name={`decision-${rc.id}`} value={value} checked={decision === value} onChange={() => setDecision(value)} />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
        {decision === 'assign_existing' ? (
          <Field label="Conversation" required>
            <Select
              placeholder="Choose conversation"
              options={rc.candidates.map((c) => ({ value: c.conversation_id, label: `Matched by ${c.match_kind.replace(/_/g, ' ')}, ${c.confidence} confidence` }))}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </Field>
        ) : null}
        {decision === 'create_new' ? (
          <div className="form-grid">
            <Field label="Title" required>
              <TextInput value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Sender type" required>
              <Select placeholder="Choose" options={domainOptions('senderType')} value={senderType} onChange={(e) => setSenderType(e.target.value as SenderType | '')} />
            </Field>
          </div>
        ) : null}
        {decision && decision !== 'dismiss' && locationIds.length > 0 ? (
          <fieldset className="stack-sm unassigned-fieldset">
            <legend className="small">Sites in message</legend>
            {locationIds.map((id) => (
              <div key={id} className="row-between" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                <LocationName locationId={id} link={false} />
                <Select
                  aria-label="Location decision"
                  placeholder="Choose"
                  options={[
                    { value: 'associate', label: 'Link site' },
                    { value: 'reject', label: 'Not related' },
                  ]}
                  value={locDecisions[id] ?? ''}
                  onChange={(e) => setLocDecisions({ ...locDecisions, [id]: e.target.value as 'associate' | 'reject' | '' })}
                />
              </div>
            ))}
          </fieldset>
        ) : null}
        <Field label="Reason" required hint="Audit log">
          <TextArea rows={3} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <p className="small muted">No mail sent. Approved drafts in affected conversations may need approval again.</p>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button type="submit" variant={decision === 'dismiss' ? 'danger' : 'primary'} disabled={missing} loading={resolve.isPending}>
            {decision === 'dismiss' ? 'Ignore message' : 'Save decision'}
          </Button>
        </div>
      </form>
    </Section>
  );
}
